import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { aboutData } from "@/mocks/aboutData";
import { useState } from "react";

const channelColorMap: Record<string, string> = {
  primary: "bg-primary-500/90",
  accent: "bg-accent-500/90",
  secondary: "bg-secondary-500/90",
};

export default function About() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [statusMsg, setStatusMsg] = useState("");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>(".hn-field");
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      setStatusMsg("订阅成功！欢迎加入深夜基地。");
      setEmail("");
      return;
    }

    if (!email.trim()) {
      setStatus("error");
      setStatusMsg("请输入有效邮箱地址");
      return;
    }

    setStatus("loading");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      formData.append("source", "about_cta");

      await fetch("https://readdy.ai/api/form/d90hoh84jh3v1cgksmbg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });

      setStatus("success");
      setStatusMsg("订阅成功！感谢加入深夜基地。");
      setEmail("");
    } catch {
      setStatus("error");
      setStatusMsg("网络异常，请稍后重试");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* About Hero */}
        <section className="w-full pt-28 pb-16 md:pt-36 md:pb-24 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-6xl mx-auto">
              {/* Left: Text */}
              <div className="flex-1 w-full">
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground-900 leading-tight">
                  关于<span className="text-primary-500">51每日大赛</span>
                </h1>
                <p className="mt-6 text-sm md:text-base text-foreground-700 leading-relaxed max-w-lg">
                  {aboutData.description}
                </p>

                <Link
                  to="/content"
                  className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  查看最新爆料
                  <i className="ri-arrow-right-line"></i>
                </Link>

                {/* Stats */}
                <div className="mt-12 flex flex-wrap gap-8 md:gap-12">
                  {aboutData.stats.map((stat) => (
                    <div key={stat.label}>
                      <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                        {stat.value}
                      </div>
                      <div className="mt-1 text-xs md:text-sm text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Image */}
              <div className="flex-1 w-full">
                <div className="relative w-full h-[350px] md:h-[450px] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20dark%20newsroom%20with%20multiple%20glowing%20monitors%20displaying%20data%20and%20trends%2C%20warm%20ambient%20lighting%2C%20professional%20editorial%20environment%2C%20cinematic%20composition%2C%20no%20people%2C%20sleek%20modern%20aesthetic%2C%20deep%20shadows%20and%20warm%20accents&width=800&height=900&seq=about-hero-01&orientation=portrait"
                    alt="51每日大赛品牌"
                    className="w-full h-full object-cover object-top"
                    title="51每日大赛品牌"
                  />
                  <div className="absolute top-5 left-5">
                    <span className="inline-block px-4 py-2 rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-900 text-sm font-heading font-bold">
                      51每日大赛
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  <div className="absolute bottom-5 left-5">
                    <p className="text-background-50 text-sm font-heading">{aboutData.tagline}</p>
                    <p className="text-background-200/80 text-xs mt-1">Since 2023</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {aboutData.teamValues.map((v) => (
                <div
                  key={v.title}
                  className="flex flex-col items-center text-center p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${v.icon} text-2xl`}></i>
                  </div>
                  <h3 className="mt-4 text-base md:text-lg font-semibold text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Channel Grid */}
        <section className="w-full bg-background-100 pb-16 md:pb-24">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight">
                <span className="text-accent-500">爆料频道全览</span>
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">吃瓜矩阵 · 全方位覆盖</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 max-w-6xl mx-auto">
              {/* Large left card */}
              {aboutData.channels
                .filter((c) => c.size === "large")
                .map((ch) => (
                  <div
                    key={ch.id}
                    className="relative row-span-2 rounded-2xl overflow-hidden group cursor-pointer h-[500px] md:h-[600px] lg:h-auto"
                  >
                    <img
                      src={ch.image}
                      alt={ch.title}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      title={ch.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-5 left-5 md:bottom-7 md:left-7">
                      <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold mb-3">
                        {ch.tag}
                      </span>
                      <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50">
                        {ch.title}
                      </h3>
                      <p className="mt-2 text-sm text-background-200/80 max-w-sm">
                        {ch.description}
                      </p>
                    </div>
                  </div>
                ))}

              {/* Right small cards */}
              <div className="flex flex-col gap-4 md:gap-5">
                {aboutData.channels
                  .filter((c) => c.size === "small")
                  .map((ch) => (
                    <div
                      key={ch.id}
                      className="relative rounded-2xl overflow-hidden group cursor-pointer h-[240px] md:h-[290px]"
                    >
                      <img
                        src={ch.image}
                        alt={ch.title}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        title={ch.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                      <div className="absolute bottom-5 left-5">
                        <span className={`inline-block px-3 py-1 rounded-full ${channelColorMap[ch.color] || channelColorMap.primary} text-background-50 text-xs font-semibold mb-2`}>
                          {ch.tag}
                        </span>
                        <h3 className="text-lg md:text-xl font-heading font-bold text-background-50">
                          {ch.title}
                        </h3>
                        <p className="mt-1 text-xs md:text-sm text-background-200/80">
                          {ch.description}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20abstract%20composition%20with%20rich%20golden%20amber%20and%20deep%20crimson%20gradients%2C%20soft%20glowing%20particles%2C%20cinematic%20mood%2C%20dramatic%20yet%20elegant%20lighting%2C%20luxurious%20texture%2C%20no%20people%2C%20artistic%20photography&width=1920&height=1080&seq=about-cta-bg-01&orientation=landscape"
              alt="开启吃瓜"
              className="w-full h-full object-cover object-top"
              title="开启吃瓜之旅"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 tracking-wider">
              START NOW
            </h2>
            <p className="mt-4 text-lg md:text-xl text-background-200/80">
              开启吃瓜之旅 · 做全网最懂瓜的人
            </p>

            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
            >
              <input
                type="text"
                name="mobile_alt"
                className="hn-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <div className="relative w-full sm:w-auto flex-1 max-w-xs">
                <div className="flex items-center bg-foreground-900 rounded-full overflow-hidden">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 flex-shrink-0 ml-1">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setStatus("idle");
                    }}
                    placeholder="输入邮箱加入吃瓜"
                    className="flex-1 bg-transparent px-4 py-3 text-sm text-background-50 placeholder-foreground-500 outline-none"
                    required
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="px-5 py-3 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:text-accent-300 transition-colors disabled:opacity-50"
                  >
                    {status === "loading" ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-arrow-right-up-line"></i>
                    )}
                  </button>
                </div>
              </div>
            </form>
            {status !== "idle" && (
              <p className={`mt-3 text-sm ${status === "success" ? "text-accent-400" : "text-primary-400"}`}>
                {statusMsg}
              </p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}